<template>
  <div v-if="data" class="text-static-default-hi">
    <Breadcrumb :category="data.category" />

    <TheMainContent>
      <RevContainer class="bg-surface-default-low pb-56 md:pb-72">
        <h1 class="heading-1 mt-32 text-center">
          {{ data.category.pageTitle }}
        </h1>
        <SuggestedCategories class="mt-56" />
      </RevContainer>
    </TheMainContent>

    <AdditionalContent
      :content="data.content"
      :custom-content-first="false"
      :relatedComparePages
    />
  </div>
</template>

<script lang="ts" setup>
import { createError, useAsyncData } from '#imports'
import { computed, defineAsyncComponent } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { RevContainer } from '@ds/components/Container'

import TheMainContent from '~/components/TheMainContent/TheMainContent.vue'

import Breadcrumb from '../../components/Breadcrumb/Breadcrumb.vue'
import SuggestedCategories from '../../components/SuggestedCategories/SuggestedCategories.vue'
import { useFetchSuggestedCategories } from '../../components/SuggestedCategories/useFetchSuggestedCategories'
import { useCurrentCategory } from '../../composables/useCurrentCategory'
import { useFetchInternalLinkCards } from '../../composables/useFetchInternalLinkCards/useFetchInternalLinkCards'
import { useModelsComparisonHead } from '../../composables/useModelsComparisonHead/useModelsComparisonHead'

const AdditionalContent = defineAsyncComponent(
  () =>
    import('../../components/Search/AdditionalContent/AdditionalContent.vue'),
)

const currentCategory = useCurrentCategory()
const $httpFetch = useHttpFetch()
const logger = useLogger()
const fetchInternalLinkCards = useFetchInternalLinkCards()

// Kick off the suggested-categories fetch in parallel so SuggestedCategories
// reuses it (same key) instead of waterfalling after this page's data.
useFetchSuggestedCategories()

const { data, error } = await useAsyncData(
  `model-comparison-search-${currentCategory}`,
  async () => {
    const [pageData, { hubRelatedComparePages }] = await Promise.all([
      $httpFetch('/bm/models-comparison/categories/:category', {
        pathParams: { category: String(currentCategory) },
      }),
      fetchInternalLinkCards(),
    ])

    return { ...pageData, relatedComparePages: hubRelatedComparePages }
  },
)

if (error.value || !data.value) {
  logger.error('[ModelComparisonSearch] Failed to fetch', {
    error: error.value,
    owners: ['bot-squad-acquisition-front'],
  })

  throw createError({
    statusCode: error.value?.statusCode ?? 404,
    statusMessage: 'Comparison category not found',
    fatal: true,
  })
}

const relatedComparePages = computed(
  () => data.value?.relatedComparePages ?? [],
)

useModelsComparisonHead(() => data.value?.content)
</script>
